import React, { useEffect, useState } from "react";
import { getWorkspaceActivityFeed } from "../../services/server";
import "../css/workspace-tools.css";

const ICON_GLYPHS = {
  "plus": "+",
  "edit": "✎",
  "user-plus": "👤",
  "user-minus": "👤",
  "activity": "⚡",
  "file-text": "📄",
  "file-check": "✓",
  "file-plus": "📎",
  "file-minus": "🗑",
  "sparkles": "✨",
  "trending-up": "📈",
  "check-circle": "✓",
  "upload": "⤴",
  "shield": "🛡",
  "clipboard": "📋",
  "check-square": "☐",
  "sticky-note": "📝",
};

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "nyss";
  if (min < 60) return `${min} min sedan`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} tim sedan`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} dgr sedan`;
  return new Date(iso).toLocaleDateString("sv-SE");
}

/**
 * ActivityFeed
 * Drop-in widget eller standalone-yta för workspace activity feed.
 *
 * Props:
 *   workspaceKey   string (slug eller orgnr)
 *   variant        "widget" | "full"   (widget = compact, scrollable)
 *   limit          number (default 50)
 *   title          string (default "Aktivitet")
 *   pollMs         number — poll-intervall för auto-refresh (0 = av)
 */
export default function ActivityFeed({
  workspaceKey,
  variant = "widget",
  limit = 50,
  title = "Aktivitet",
  pollMs = 0,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!workspaceKey) return;
    let cancelled = false;
    let timer = null;

    const load = async () => {
      try {
        const data = await getWorkspaceActivityFeed(workspaceKey, { limit });
        if (cancelled) return;
        setItems(Array.isArray(data.items) ? data.items : []);
        setErr("");
      } catch (e) {
        if (cancelled) return;
        setErr(e?.message || "Kunde inte hämta aktivitet");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    if (pollMs > 0) {
      timer = setInterval(load, pollMs);
    }
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [workspaceKey, limit, pollMs]);

  return (
    <section className="wt-card">
      <h3 className="wt-card__title">{title}</h3>
      {loading && <p className="wt-empty">Laddar…</p>}
      {!loading && err && <p className="wt-empty">{err}</p>}
      {!loading && !err && items.length === 0 && (
        <p className="wt-empty">Inga händelser ännu.</p>
      )}
      {!loading && items.length > 0 && (
        <ul className={`wt-feed ${variant === "widget" ? "wt-feed--widget" : ""}`}>
          {items.map((it) => (
            <li key={it.id} className="wt-feed__item">
              <div className={`wt-feed__icon wt-feed__icon--${it.severity}`}>
                {ICON_GLYPHS[it.icon] || "•"}
              </div>
              <div className="wt-feed__body">
                <p className="wt-feed__text">{it.text}</p>
                <span className="wt-feed__meta">{timeAgo(it.at)}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
